import {
  computed,
  defineStore,
  reactive,
  ref,
  useMetrics,
  useSupabase,
  useUser,
} from "#imports";
import type { Tables } from "~~/types/database.types";

export const useBookmarks = defineStore("bookmarks", () => {
  const supabase = useSupabase();
  const userStore = useUser();
  const { trackEvent } = useMetrics();

  const viewedSpots = ref<number>(0);

  const isMapFetched = ref<boolean>(false);
  const mapBookmarks = reactive<Tables<"map_bookmarks">[]>([]);
  const fetchMapBookmarks = async () => {
    if (!userStore.user.id) {
      mapBookmarks.length = 0;
      return;
    }

    if (isMapFetched.value) {
      return;
    }

    const { data } = await supabase
      .from("map_bookmarks")
      .select("*")
      .eq("user_id", userStore.user.id)
      .throwOnError();

    Object.assign(mapBookmarks, data);
    isMapFetched.value = true;
  };

  const isMapBookmarked = (map_id: string) =>
    computed(() => !!mapBookmarks.find((f) => f.map_id === map_id));

  const addMapBookmark = async (map_id: string) => {
    if (!userStore.user.id) {
      throw new Error("Need to login before you can add a bookmark");
    }
    const { data } = await supabase
      .from("map_bookmarks")
      .insert({ map_id, user_id: userStore.user.id })
      .select("*")
      .throwOnError();
    mapBookmarks.push(data[0]!);
    trackEvent("bookmarked_map", {
      map_id,
    });
  };

  const removeMapBookmark = async (map_id: string) => {
    if (!userStore.user.id) {
      throw new Error("Need to login before you can remove a bookmark");
    }
    await supabase
      .from("map_bookmarks")
      .delete()
      .eq("map_id", map_id)
      .eq("user_id", userStore.user.id)
      .throwOnError();

    mapBookmarks.splice(
      mapBookmarks.findIndex((f) => f.map_id === map_id),
      1,
    );
  };

  const isSpotFetched = ref<boolean>(false);
  const spotBookmarks = reactive<Tables<"spot_bookmarks">[]>([]);
  const fetchSpotBookmarks = async () => {
    if (!userStore.user.id) {
      mapBookmarks.length = 0;
      return;
    }

    if (isSpotFetched.value) {
      return;
    }

    const { data } = await supabase
      .from("spot_bookmarks")
      .select("*")
      .eq("user_id", userStore.user.id)
      .throwOnError();

    Object.assign(spotBookmarks, data);
    isSpotFetched.value = true;
  };

  const isSpotBookmarked = (spot_id: string) =>
    computed(() => !!spotBookmarks.find((f) => f.spot_id === spot_id));

  const addSpotBookmark = async (spot_id: string) => {
    if (!userStore.user.id) {
      throw new Error("Need to login before you can add a bookmark");
    }
    const { data } = await supabase
      .from("spot_bookmarks")
      .insert({ spot_id, user_id: userStore.user.id })
      .select("*")
      .throwOnError();
    spotBookmarks.push(data[0]!);
    trackEvent("bookmarked_spot", {
      spot_id,
    });
  };

  const removeSpotBookmark = async (spot_id: string) => {
    if (!userStore.user.id) {
      throw new Error("Need to login before you can remove a bookmark");
    }
    await supabase
      .from("spot_bookmarks")
      .delete()
      .eq("spot_id", spot_id)
      .eq("user_id", userStore.user.id)
      .throwOnError();

    spotBookmarks.splice(
      spotBookmarks.findIndex((f) => f.spot_id === spot_id),
      1,
    );
  };

  return {
    isMapFetched,
    fetchMapBookmarks,
    mapBookmarks,
    isMapBookmarked,
    addMapBookmark,
    removeMapBookmark,

    isSpotFetched,
    fetchSpotBookmarks,
    spotBookmarks,
    isSpotBookmarked,
    addSpotBookmark,
    removeSpotBookmark,

    viewedSpots,
  };
});
